export const SILENT_AFTER_MS = 3_000;
// A signal-back run clears the banner and disarms the detector. Well below speech level
// on purpose: an SDR who fixed the mute and speaks softly must not keep a red banner.
export const CLEAR_MIN = 0.02;
export const CLEAR_RUN = 3;
export const QUIET_MAX = 0.05;
// A throttled tab can deliver samples seconds apart; one late sample must not raise.
const MAX_GAP_MS = 250;

export type MicSilenceSource = 'detector' | 'sdk';

interface SilentMic {
  remoteAudibleAt: number | null;
  silentMs: number;
  windowMax: number;
  signalRun: number;
  signalSeen: boolean;
  lastAt: number | null;
  detectorFired: boolean;
  raised: { source: MicSilenceSource; at: number } | null;
}

export function newSilentMic(): SilentMic {
  return {
    remoteAudibleAt: null,
    silentMs: 0,
    windowMax: 0,
    signalRun: 0,
    signalSeen: false,
    lastAt: null,
    detectorFired: false,
    raised: null,
  };
}

export interface MicSample {
  input: number;
  remoteAudible: boolean;
  now: number;
}

export type MicTransition =
  | {
      type: 'raised';
      source: MicSilenceSource;
      windowMax: number | null;
      msSinceRemote: number | null;
    }
  | { type: 'observed_quiet'; windowMax: number; msSinceRemote: number }
  | {
      type: 'cleared';
      reason: 'signal' | 'sdk_cleared';
      source: MicSilenceSource;
      msSinceRaise: number;
    };

function clear(s: SilentMic, reason: 'signal' | 'sdk_cleared', now: number): MicTransition | null {
  const r = s.raised;
  if (!r) return null;
  s.raised = null;
  return { type: 'cleared', reason, source: r.source, msSinceRaise: Math.round(now - r.at) };
}

export function observeMic(s: SilentMic, x: MicSample): MicTransition | null {
  const gap = s.lastAt == null ? 0 : Math.min(Math.max(0, x.now - s.lastAt), MAX_GAP_MS);
  s.lastAt = x.now;

  if (x.input > CLEAR_MIN) s.signalRun += 1;
  else s.signalRun = 0;
  if (s.signalRun >= CLEAR_RUN) {
    // A working mic can read zero while its owner listens, so signal at any point proves it live.
    s.signalSeen = true;
    const cleared = clear(s, 'signal', x.now);
    if (cleared) return cleared;
  }

  if (s.remoteAudibleAt == null) {
    if (x.remoteAudible) s.remoteAudibleAt = x.now;
    return null;
  }
  if (s.signalSeen || s.detectorFired) return null;

  s.silentMs += gap;
  s.windowMax = Math.max(s.windowMax, x.input);
  if (s.silentMs < SILENT_AFTER_MS) return null;

  s.detectorFired = true;
  const msSinceRemote = Math.round(x.now - s.remoteAudibleAt);
  // The analyser floors at −100 dBFS and noise suppression never reaches it, so only a dead
  // input reads exactly zero throughout. A low mic is logged, not shown.
  if (s.windowMax === 0) {
    if (s.raised) return null;
    s.raised = { source: 'detector', at: x.now };
    return { type: 'raised', source: 'detector', windowMax: 0, msSinceRemote };
  }
  if (s.windowMax < QUIET_MAX) {
    return { type: 'observed_quiet', windowMax: s.windowMax, msSinceRemote };
  }
  return null;
}

export function raiseFromSdk(s: SilentMic, now: number): MicTransition | null {
  if (s.raised) return null;
  s.raised = { source: 'sdk', at: now };
  return {
    type: 'raised',
    source: 'sdk',
    windowMax: null,
    msSinceRemote: s.remoteAudibleAt == null ? null : Math.round(now - s.remoteAudibleAt),
  };
}

export function clearFromSdk(s: SilentMic, now: number): MicTransition | null {
  return s.raised?.source === 'sdk' ? clear(s, 'sdk_cleared', now) : null;
}
